// src/containers/MonitoringContainer.js
import '../components/MonitoringContainer.css';
import React, { useState, useEffect } from 'react';
import { SENSOR_COLORS, STATUS_LABELS } from '../constants/colors';
import useSensorMonitoring from '../hooks/useSensorMonitoring';
import { fetchMachines } from '../services/sensorService';

const MonitoringContainer = () => {
  const { sensorData, loading, reload } = useSensorMonitoring();
  const [currentPage, setCurrentPage] = useState(0);
  const [isAutoRotating, setIsAutoRotating] = useState(true);
  const [rotationInterval, setRotationInterval] = useState(10); // 초 단위
  const GROUPS_PER_PAGE = 1; // 페이지당 그룹 수 (동적 조정)
  const [machines, setMachines] = useState([]);

  // 설비 목록 로드
  useEffect(() => {
    const loadMachines = async () => {
      try {
        const data = await fetchMachines();
        setMachines(data);
      } catch (error) {
        console.error('설비 목록 로드 오류:', error);
      }
    };
    loadMachines();
  }, []);

  const getZoneName = (machine) => {
    if (machine.startsWith('A-BAN-1')) {
      const sensorNum = parseInt(machine.split(' ')[1]);
      if (sensorNum >= 1 && sensorNum <= 4) {
        return '예열존';
      } else if (sensorNum >= 5 && sensorNum <= 8) {
        return '가열존';
      }
    } else if (machine.startsWith('C-BAN-8')) {
      const sensorNum = parseInt(machine.split(' ')[1]);
      if (sensorNum === 1) {
        return '예열존';
      } else {
        return '가열존';
      }
    } else if (machine.startsWith('A-BAN-4')) {
      const sensorNum = parseInt(machine.split(' ')[1]);
      if (sensorNum === 1 || sensorNum === 2) {
        return '예열존';
      } else {
        return '가열존';
      }
    }
    return null;
  };

  // 페이지별 설비 그룹 정의 (MachineGroupManager와 동일)
  const machinePages = {
    1: ['A-BAN-1'], // A동 열처리만 넓게 1페이지
    2: ['C-BAN-8', 'A-BAN-4'], // 4-1호기(왼쪽), 4-2호기(오른쪽)
    3: ['BA-2', 'BA-3', 'BA-4'],
    5: ['BB-1', 'BB-2', 'BB-3', 'BB-4']
  };

  const getGroupedSensors = () => {
    const groups = {};
    
    // DB에서 가져온 센서 데이터를 그룹화
    sensorData.forEach(sensor => {
      const groupName = sensor.machine.split(' ')[0];
      
      if (!groups[groupName]) {
        groups[groupName] = [];
      }
      
      groups[groupName].push(sensor);
    });
    
    return groups;
  };

  const groupedSensors = getGroupedSensors();
  const groupEntries = Object.entries(groupedSensors);

  const getPageGroups = () => {
    const pages = [];
    
    // 정의된 페이지별로 그룹 구성
    Object.keys(machinePages).forEach(pageNum => {
      const pageMachineNames = machinePages[pageNum];
      const pageGroups = [];
      
      pageMachineNames.forEach(machineName => {
        // 해당 머신 이름으로 시작하는 센서들 찾기
        const machineSensors = groupEntries.find(([groupName]) => 
          groupName === machineName || groupName.startsWith(machineName)
        );
        
        if (machineSensors) {
          pageGroups.push(machineSensors);
        }
      });
      
      if (pageGroups.length > 0) {
        pages.push(pageGroups);
      }
    });
    
    return pages;
  };

  const pageGroups = getPageGroups();
  const totalPages = pageGroups.length;

  // 머신 이름을 표시용 이름으로 변환
  const getDisplayName = (machineName) => {
    const nameMap = {
      'A-BAN-1': 'A동 열처리',
      'A-BAN-4': '4-2호기',
      'C-BAN-8': '4-1호기'
    };
    return nameMap[machineName] || machineName;
  };

  // 설비 활성 상태 확인
  const getMachineStatus = (machineName) => {
    const machine = machines.find(m => m.machine_name === machineName);
    return machine ? Boolean(machine.is_active ?? 1) : true;
  };

  // 자동 로테이션
  useEffect(() => {
    if (!isAutoRotating || totalPages <= 1) return;
    
    const interval = setInterval(() => {
      setCurrentPage(prev => (prev + 1) % totalPages);
    }, rotationInterval * 1000);
    
    return () => clearInterval(interval);
  }, [isAutoRotating, totalPages, rotationInterval]);
  
  const getCurrentPageGroups = () => {
    return pageGroups[currentPage] || [];
  };

  const getStatusColor = (temp, max, min, ispass) => {
    if (temp === null) return SENSOR_COLORS.NO_DATA;
    if (!ispass) return SENSOR_COLORS.ABNORMAL;
    if (temp < min + 5) return SENSOR_COLORS.LOW_TEMP;
    if (temp > max - 5) return SENSOR_COLORS.HIGH_TEMP;
    return SENSOR_COLORS.NORMAL;
  };

  return (
    <div className="monitoring-container">
      <div className="monitoring-header">
        <div className="header-left">
          <h2>온도 센서 모니터링 ({sensorData.length}개)</h2>
          <div className="last-update">
            {loading ? '갱신중...' : `마지막 갱신: ${new Date().toLocaleString()}`}
          </div>
        </div>
        <div className="header-controls">
          <div className="page-info">
            {totalPages > 1 && (
              <span>{currentPage + 1}페이지 / {totalPages}페이지</span>
            )}
          </div>
          <div className="control-buttons">
            {totalPages > 1 && (
              <>
                <button 
                  onClick={() => setCurrentPage(prev => (prev - 1 + totalPages) % totalPages)}
                  className="nav-button"
                >
                  ◀
                </button>
                <button 
                  onClick={() => setCurrentPage(prev => (prev + 1) % totalPages)}
                  className="nav-button"
                >
                  ▶
                </button>
                <div className="interval-setting">
                  <input 
                    type="number" 
                    min="5" 
                    max="60" 
                    value={rotationInterval}
                    onChange={(e) => setRotationInterval(Number(e.target.value))}
                    className="interval-input"
                  />
                  <span className="interval-label">초</span>
                </div>
                <button 
                  onClick={() => setIsAutoRotating(!isAutoRotating)}
                  className={`auto-button ${isAutoRotating ? 'active' : ''}`}
                >
                  {isAutoRotating ? '자동 ON' : '자동 OFF'}
                </button>
              </>
            )}
            <button 
              onClick={() => {
                const popup = window.open('/monitoring', 'monitoring', 
                  'width=1920,height=1080,scrollbars=yes,resizable=yes,toolbar=no,menubar=no,location=no,status=no');
                if (popup) popup.focus();
              }}
              className="fullscreen-button"
            >
              📺 실시간 모니터링
            </button>
            <button onClick={reload} disabled={loading} className="refresh-button">새로고침</button>
          </div>
        </div>
      </div>

      <div className={`monitoring-groups page-${currentPage + 1}`}>
        {getCurrentPageGroups().map(([groupName, sensors]) => (
          <div key={groupName} className={`monitoring-group group-${sensors.length}`}>
            <div className="group-header">
              <h3>{getDisplayName(groupName)}</h3>
              <div className="badge-container">
                <span className="count-badge">{sensors.length}개</span>
                {!getMachineStatus(groupName) && (
                  <span className="inactive-badge">비가동</span>
                )}
              </div>
            </div>
            
            <div className={`monitoring-grid grid-${sensors.length}`}>
              {sensors.map((sensor, idx) => {
                const colorClass = sensor.temp === null
                  ? 'normal'
                  : !sensor.ispass
                    ? 'abnormal'
                    : sensor.temp > sensor.max
                      ? 'high-temp'
                      : 'normal';

                return (
                  <div
                    key={idx}
                    className={`monitoring-box ${colorClass}`}
                    style={{
                      backgroundColor: getStatusColor(sensor.temp, sensor.max, sensor.min, sensor.ispass)
                    }}
                  >
                    {(() => {
                      const zone = getZoneName(sensor.machine);
                      if (!zone) return null;
                      return (
                        <div
                          className="zone-badge"
                          style={{
                            position: 'absolute',
                            top: 8,
                            left: 8,
                            background: zone === '예열존' ? '#e3f2fd' : '#fff3e0',
                            color: zone === '예열존' ? '#1976d2' : '#f57c00',
                            padding: '10px 16px',
                            borderRadius: 16,
                            fontSize: 18,
                            fontWeight: 700,
                            boxShadow: '0 3px 12px rgba(0,0,0,0.2)',
                            border: `3px solid ${zone === '예열존' ? '#1976d2' : '#f57c00'}`,
                            zIndex: 10
                          }}
                        >
                          {zone}
                        </div>
                      );
                    })()}
                    <div className="monitoring-machine">{sensor.machine}</div>
                    <div className="monitoring-temp">
                      {sensor.temp !== null ? `${sensor.temp}°C` : 'N/A'}
                    </div>
                    <div className="monitoring-range">
                      상한: {sensor.max}°C | 하한: {sensor.min}°C
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      <div className="monitoring-legend">
        {STATUS_LABELS.map((status, idx) => (
          <span key={idx}>
            <span style={{ color: status.color }}>●</span> {status.label}
          </span>
        ))}
      </div>
    </div>
  );
};

export default MonitoringContainer;
